import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check, FileText, Lock, X } from 'lucide-react';
import {
  declineApprovalPortal,
  extractPortalToken,
  getApprovalPortalDocumentUrl,
  listApprovalPortalDocuments,
  signApprovalPortal,
  verifyApprovalPortal,
} from '../../utils/providerApprovalPortalApi';

const PAGE_SX = {
  minHeight: '100vh',
  bgcolor: '#fff',
  '& .MuiTextField-root': {
    maxWidth: '100%',
  },
  '& .MuiInputBase-root': {
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input': {
    border: 0,
    boxShadow: 'none',
    borderRadius: 0,
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input:focus': {
    border: 0,
    boxShadow: 'none',
  },
};
const TOP_BAR_SX = {
  height: 56,
  borderBottom: '1px solid rgba(0,0,0,0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: { xs: 2, sm: 4 },
};
const BRAND_SX = { display: 'flex', alignItems: 'center', gap: 1.25 };
const BRAND_MARK_SX = {
  width: 28,
  height: 28,
  bgcolor: '#000',
  color: '#fff',
  borderRadius: 1,
  display: 'grid',
  placeItems: 'center',
  fontSize: 13,
  fontWeight: 800,
};
const WRAP_SX = { maxWidth: 760, mx: 'auto', px: { xs: 2, sm: 4 }, py: 5 };
const TITLE_SX = { fontSize: { xs: 28, sm: 34 }, fontWeight: 800, letterSpacing: 0 };
const BODY_SX = { fontSize: 14, color: '#475569', lineHeight: 1.6, mt: 1 };
const PANEL_SX = { border: '1px solid #000', p: { xs: 2, sm: 3 }, mt: 3 };
const DOC_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 1.5,
  borderTop: '1px solid rgba(0,0,0,0.08)',
  '&:first-of-type': { borderTop: 0 },
};
const DOC_ICON_SX = {
  width: 34,
  height: 42,
  border: '1px solid #000',
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};
const MONO_INPUT_SX = {
  fontFamily: 'Roboto Mono, ui-monospace, SFMono-Regular, monospace',
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  fallback;

const documentName = (doc) =>
  doc?.document_name || doc?.filename || doc?.original_filename || 'Document';

export default function ProviderApprovalPortalPage() {
  const token = useMemo(() => extractPortalToken(window.location.hash), []);
  const [npiLast4, setNpiLast4] = useState('');
  const [providerName, setProviderName] = useState('');
  const [documents, setDocuments] = useState([]);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [docLoadingId, setDocLoadingId] = useState(null);
  const [signature, setSignature] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    document.title = 'Secure approval request';
  }, []);

  const loadDocuments = async () => {
    const res = await listApprovalPortalDocuments();
    setProviderName(res.data?.provider_name || providerName);
    setDocuments(res.data?.documents || []);
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!token) {
      setError('This approval link is missing its secure token.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await verifyApprovalPortal({ token, npiLast4 });
      setProviderName(res.data?.provider_name || '');
      setVerified(true);
      await loadDocuments();
    } catch (err) {
      setError(errorMessage(err, 'Identity verification failed.'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDocument = async (doc) => {
    setDocLoadingId(doc.id);
    setError('');
    try {
      const res = await getApprovalPortalDocumentUrl(doc.id);
      window.open(res.data?.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(errorMessage(err, 'Could not open this document.'));
    } finally {
      setDocLoadingId(null);
    }
  };

  const handleSign = async (event) => {
    event.preventDefault();
    if (!signature.trim()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await signApprovalPortal({ signatureData: signature.trim() });
      setSuccess('Approval signed. You can close this window.');
    } catch (err) {
      setError(errorMessage(err, 'Could not sign this approval.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await declineApprovalPortal({ declineReason: declineReason.trim() });
      setSuccess('Approval declined. The sender has been notified.');
    } catch (err) {
      setError(errorMessage(err, 'Could not decline this approval.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="ab" data-testid="provider-approval-portal" sx={PAGE_SX}>
      <Box sx={TOP_BAR_SX}>
        <Box sx={BRAND_SX}>
          <Box sx={BRAND_MARK_SX}>M</Box>
          <Typography fontWeight={800}>Meduri</Typography>
        </Box>
        <Typography fontSize={12} color="text.secondary">
          Secure approval portal
        </Typography>
      </Box>

      <Box sx={WRAP_SX}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Lock size={18} />
          <Typography fontSize={12} fontWeight={800} letterSpacing="0.08em">
            PRIVATE LINK
          </Typography>
        </Stack>
        <Typography sx={TITLE_SX}>
          Review and sign approval documents
        </Typography>
        <Typography sx={BODY_SX}>
          Verify your identity, review the attached documents, then sign or decline
          the request. Documents open through short-lived secure links.
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}

        {!verified ? (
          <Box component="form" sx={PANEL_SX} onSubmit={handleVerify}>
            <Typography fontWeight={800} fontSize={18}>Identity verification</Typography>
            <Typography sx={BODY_SX}>
              Enter the last 4 digits of your NPI if requested by the email.
            </Typography>
            <TextField
              label="NPI last 4"
              value={npiLast4}
              onChange={(event) => setNpiLast4(event.target.value.replace(/\D/g, '').slice(0, 4))}
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              inputProps={{ inputMode: 'numeric', pattern: '\\d{4}', style: MONO_INPUT_SX }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 2, bgcolor: '#000', '&:hover': { bgcolor: '#111' } }}
              disabled={loading || !token}
              startIcon={loading ? <CircularProgress size={16} /> : <Lock size={16} />}
            >
              Verify identity
            </Button>
          </Box>
        ) : (
          <Box sx={PANEL_SX}>
            <Typography fontWeight={800} fontSize={18}>
              {providerName ? `Hello, ${providerName}` : 'Approval documents'}
            </Typography>
            <Typography sx={BODY_SX}>
              Review each document before signing.
            </Typography>

            <Box sx={{ mt: 2 }}>
              {documents.length === 0 ? (
                <Typography color="text.secondary" fontSize={14}>
                  No documents are attached to this approval.
                </Typography>
              ) : documents.map((doc) => (
                <Box key={doc.id} sx={DOC_ROW_SX}>
                  <Box sx={DOC_ICON_SX}>
                    <FileText size={16} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>{documentName(doc)}</Typography>
                    <Typography fontSize={12} color="text.secondary">Secure document</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenDocument(doc)}
                    disabled={docLoadingId === doc.id || loading}
                  >
                    {docLoadingId === doc.id ? 'Opening...' : 'Open'}
                  </Button>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box component="form" onSubmit={handleSign}>
              <TextField
                label="Typed signature"
                value={signature}
                onChange={(event) => setSignature(event.target.value)}
                size="small"
                fullWidth
                helperText="Type your full name to sign."
              />
              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 2, mr: 1, bgcolor: '#000', '&:hover': { bgcolor: '#111' } }}
                disabled={loading || !signature.trim() || Boolean(success)}
                startIcon={<Check size={16} />}
              >
                Sign approval
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <TextField
              label="Decline reason"
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={3}
            />
            <Button
              variant="outlined"
              color="error"
              sx={{ mt: 2 }}
              disabled={loading || !declineReason.trim() || Boolean(success)}
              startIcon={<X size={16} />}
              onClick={handleDecline}
            >
              Decline approval
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
